import React from 'react';
import {
  Drawer,
  Box,
  IconButton,
  Typography,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  CircularProgress,
  Alert,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/CloseRounded';
import WhatsAppIcon from '@mui/icons-material/WhatsApp';
import StorefrontRoundedIcon from '@mui/icons-material/StorefrontRounded';
import TwoWheelerRoundedIcon from '@mui/icons-material/TwoWheelerRounded';
import LocalHospitalRoundedIcon from '@mui/icons-material/LocalHospitalRounded';
import DescriptionRoundedIcon from '@mui/icons-material/DescriptionRounded';
import OpenInNewRoundedIcon from '@mui/icons-material/OpenInNewRounded';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { statusStyle } from '../../theme/orderStatus';
import {
  getPharmacyChainOrder,
  updatePharmacyChainOrderStatus,
  PharmacyChainOrderItem,
  OrderStatus,
} from '../../api/pharmacyChainOrders';

const STATUS_OPTIONS: OrderStatus[] = [
  'PENDING',
  'IN_PROGRESS',
  'READY_FOR_PICKUP',
  'COMPLETED',
  'CANCELLED',
];

const STATUS_LABEL_KEY: Record<OrderStatus, string> = {
  PENDING: 'status.pending',
  IN_PROGRESS: 'status.inProgress',
  READY_FOR_PICKUP: 'status.readyForPickup',
  COMPLETED: 'status.completed',
  CANCELLED: 'status.cancelled',
};

interface Props {
  orderId: string | null;
  onClose: () => void;
  /**
   * Renders the panel as a lookup surface with no write controls. The report
   * opens this panel over orders that have already reached a terminal state:
   * a mis-click on the status select there would reopen a completed order onto
   * the live kanban and drop it out of the report being read. Defaults to
   * false so the kanban — where changing status is the entire point — is
   * unaffected.
   */
  readOnly?: boolean;
}

const Eyebrow: React.FC<{ children: React.ReactNode; sx?: any }> = ({ children, sx }) => (
  <Typography
    component="span"
    sx={{
      fontFamily: '"Plus Jakarta Sans", sans-serif',
      fontSize: '0.68rem',
      fontWeight: 700,
      letterSpacing: '0.2em',
      textTransform: 'uppercase',
      color: 'text.secondary',
      lineHeight: 1,
      display: 'block',
      ...sx,
    }}
  >
    {children}
  </Typography>
);

const SectionHeader: React.FC<{ label: string; sx?: any }> = ({ label, sx }) => (
  <Box sx={{ mb: 1.5, ...sx }}>
    <Eyebrow>{label}</Eyebrow>
    <Box sx={{ mt: 0.75, height: '1px', bgcolor: 'divider' }} />
  </Box>
);

export const PharmacyOrderDetailPanel: React.FC<Props> = ({ orderId, onClose, readOnly = false }) => {
  const { t } = useTranslation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const queryClient = useQueryClient();

  const detailQuery = useQuery({
    queryKey: ['pharmacy-chain-order', orderId],
    queryFn: () => getPharmacyChainOrder(orderId!),
    enabled: !!orderId,
  });

  const statusMutation = useMutation({
    mutationFn: (status: OrderStatus) =>
      updatePharmacyChainOrderStatus(orderId!, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['pharmacy-chain-orders'] });
      queryClient.invalidateQueries({ queryKey: ['pharmacy-chain-order', orderId] });
      // A status change moves an order in or out of the terminal set the
      // manager report counts. No-op wherever no report query is mounted.
      queryClient.invalidateQueries({ queryKey: ['pharmacy-reports'] });
    },
  });

  const data = detailQuery.data;
  const currentStatusStyle = data ? statusStyle(data.status) : null;

  return (
    <Drawer
      anchor={isMobile ? 'bottom' : 'right'}
      open={!!orderId}
      onClose={onClose}
      PaperProps={{
        sx: isMobile
          ? {
              height: '92vh',
              borderTopLeftRadius: 24,
              borderTopRightRadius: 24,
              p: 0,
              bgcolor: 'background.default',
              background:
                'linear-gradient(180deg, #F4F7FC 0%, #EEF2F8 14%, #EEF2F8 100%)',
            }
          : {
              width: { sm: 480, md: 560 },
              maxWidth: '95vw',
              bgcolor: 'background.default',
              background:
                'linear-gradient(180deg, #F4F7FC 0%, #EEF2F8 90px)',
            },
      }}
    >
      {/* Mobile drag-handle */}
      {isMobile && (
        <Box sx={{ pt: 1.25, pb: 0.5, display: 'flex', justifyContent: 'center' }}>
          <Box
            sx={{
              width: 44,
              height: 5,
              borderRadius: 999,
              bgcolor: 'grey.300',
            }}
          />
        </Box>
      )}

      <Box
        sx={{
          px: { xs: 2.5, sm: 3.5 },
          pt: { xs: 1, sm: 3 },
          pb: { xs: 3, sm: 4 },
          height: '100%',
          overflow: 'auto',
        }}
      >
        {/* Sticky-ish header row */}
        <Box
          sx={{
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'flex-start',
            mb: 2,
          }}
        >
          <Eyebrow sx={{ color: 'primary.main', mt: 0.75 }}>
            {t('kanban.trackingNumber')}
          </Eyebrow>
          <IconButton
            onClick={onClose}
            aria-label={t('kanban.closePanel') as string}
            sx={{
              border: '1px solid',
              borderColor: 'divider',
              bgcolor: 'background.paper',
              width: 36,
              height: 36,
              '&:hover': { bgcolor: 'primary.softBg' },
            }}
          >
            <CloseIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>

        {detailQuery.isLoading && (
          <Box sx={{ textAlign: 'center', py: 8 }}>
            <CircularProgress size={28} sx={{ color: 'text.headline' }} />
            <Typography variant="body2" sx={{ mt: 2, color: 'text.secondary' }}>
              {t('kanban.loadingDetail')}
            </Typography>
          </Box>
        )}

        {detailQuery.isError && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {t('kanban.detailNotFound')}
          </Alert>
        )}

        {data && currentStatusStyle && (
          <>
            {/* Identity block — patient name + tracking + status pill */}
            <Box sx={{ mb: 3 }}>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", "SFMono-Regular", Menlo, monospace',
                  fontSize: '0.78rem',
                  letterSpacing: '0.08em',
                  color: 'text.disabled',
                  mb: 0.75,
                }}
              >
                #{data.trackingUUID.slice(0, 8).toUpperCase()}
              </Typography>
              <Typography
                sx={{
                  fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
                  fontWeight: 500,
                  fontSize: { xs: '1.7rem', sm: '2rem' },
                  lineHeight: 1.15,
                  letterSpacing: '-0.02em',
                  color: 'text.headline',
                  mb: 1.5,
                }}
              >
                {data.patientName}
              </Typography>
              {data.patientPhone && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1.5 }}>
                  <Typography
                    variant="body2"
                    sx={{
                      color: 'text.secondary',
                      fontVariant: 'tabular-nums',
                    }}
                  >
                    {t('kanban.phone')}: {data.patientPhone}
                  </Typography>
                  <IconButton
                    component="a"
                    href={`https://wa.me/${data.patientPhone.replace(/\D/g, '')}`}
                    target="_blank"
                    rel="noopener noreferrer"
                    size="small"
                    aria-label={t('kanban.whatsappCustomer') as string}
                    sx={{
                      color: '#25D366',
                      border: '1px solid',
                      borderColor: '#25D36633',
                      bgcolor: '#25D36610',
                      width: 30,
                      height: 30,
                      '&:hover': { bgcolor: '#25D36622' },
                    }}
                  >
                    <WhatsAppIcon fontSize="small" />
                  </IconButton>
                </Box>
              )}
              <Box
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 1,
                  px: 1.5,
                  py: 0.75,
                  borderRadius: 999,
                  bgcolor: currentStatusStyle.bg,
                  color: currentStatusStyle.fg,
                }}
              >
                <Box
                  sx={{
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    bgcolor: currentStatusStyle.fg,
                  }}
                />
                <Typography
                  sx={{
                    fontFamily: '"Plus Jakarta Sans", sans-serif',
                    fontSize: '0.78rem',
                    fontWeight: 700,
                    letterSpacing: '0.05em',
                    color: currentStatusStyle.fg,
                    lineHeight: 1,
                  }}
                >
                  {currentStatusStyle.label}
                </Typography>
              </Box>
            </Box>

            {/* Change status select — omitted entirely in read-only mode */}
            {!readOnly && (
            <Box sx={{ mb: 3.5 }}>
              <FormControl fullWidth size="small" disabled={statusMutation.isPending}>
                <InputLabel id="status-select-label">{t('kanban.changeStatus')}</InputLabel>
                <Select
                  labelId="status-select-label"
                  label={t('kanban.changeStatus')}
                  value={data.status}
                  onChange={(e) => statusMutation.mutate(e.target.value as OrderStatus)}
                  sx={{
                    bgcolor: 'background.paper',
                    fontWeight: 600,
                    color: 'text.headline',
                    '& .MuiSelect-select': { py: 1.25 },
                  }}
                >
                  {STATUS_OPTIONS.map((s) => {
                    const style = statusStyle(s);
                    return (
                      <MenuItem key={s} value={s}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                          <Box
                            sx={{
                              width: 8,
                              height: 8,
                              borderRadius: '50%',
                              bgcolor: style.fg,
                            }}
                          />
                          {t(STATUS_LABEL_KEY[s])}
                        </Box>
                      </MenuItem>
                    );
                  })}
                </Select>
              </FormControl>
            </Box>
            )}

            {/* Doctor */}
            {data.doctor && (
              <Box sx={{ mb: 3.5 }}>
                <SectionHeader label={t('kanban.doctor')} />
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                  <Box
                    sx={{
                      width: 36,
                      height: 36,
                      borderRadius: '50%',
                      bgcolor: 'primary.softBg',
                      color: 'primary.dark',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <LocalHospitalRoundedIcon sx={{ fontSize: 18 }} />
                  </Box>
                  <Box>
                    <Typography
                      sx={{
                        fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
                        fontSize: '1.05rem',
                        fontWeight: 500,
                        color: 'text.headline',
                        lineHeight: 1.25,
                      }}
                    >
                      Dr. {data.doctor.firstName} {data.doctor.lastName}
                    </Typography>
                    <Typography variant="caption" sx={{ color: 'text.secondary' }}>
                      {t('kanban.license')}: {data.doctor.licenseNumber}
                    </Typography>
                  </Box>
                </Box>
              </Box>
            )}

            {/* Fulfillment */}
            <Box sx={{ mb: 3.5 }}>
              <SectionHeader label={t('kanban.fulfillment')} />
              <Box
                sx={{
                  display: 'flex',
                  alignItems: 'flex-start',
                  gap: 1.5,
                  p: 1.75,
                  bgcolor: 'background.paper',
                  border: '1px solid',
                  borderColor: 'divider',
                  borderRadius: 2,
                }}
              >
                <Box
                  sx={{
                    width: 36,
                    height: 36,
                    borderRadius: '50%',
                    bgcolor: 'primary.softBg',
                    color: 'primary.dark',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    flexShrink: 0,
                  }}
                >
                  {data.fulfillmentType === 'PICKUP' ? (
                    <StorefrontRoundedIcon sx={{ fontSize: 18 }} />
                  ) : (
                    <TwoWheelerRoundedIcon sx={{ fontSize: 18 }} />
                  )}
                </Box>
                <Box sx={{ flex: 1, minWidth: 0 }}>
                  {data.fulfillmentType === 'PICKUP' ? (
                    <>
                      <Typography
                        sx={{
                          fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
                          fontSize: '1.05rem',
                          fontWeight: 500,
                          color: 'text.headline',
                          lineHeight: 1.25,
                          mb: 0.25,
                        }}
                      >
                        {data.pickupBranch?.name ?? t('kanban.pickup')}
                      </Typography>
                      {data.pickupBranch && (
                        <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block' }}>
                          {data.pickupBranch.address}
                        </Typography>
                      )}
                    </>
                  ) : (
                    <>
                      <Typography
                        sx={{
                          fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
                          fontSize: '1.05rem',
                          fontWeight: 500,
                          color: 'text.headline',
                          lineHeight: 1.25,
                          mb: 0.25,
                        }}
                      >
                        {t('kanban.delivery')}
                      </Typography>
                      <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                        {data.deliveryAddress}
                      </Typography>
                      {data.deliveryNotes && (
                        <Typography
                          variant="caption"
                          sx={{ color: 'text.disabled', display: 'block', mt: 0.5 }}
                        >
                          {data.deliveryNotes}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Box>
            </Box>

            {/* Customer notes */}
            {data.notes && (
              <Box sx={{ mb: 3.5 }}>
                <SectionHeader label={t('kanban.customerNotes')} />
                <Box
                  sx={{
                    p: 1.75,
                    bgcolor: 'background.paper',
                    border: '1px solid',
                    borderColor: 'divider',
                    borderRadius: 2,
                  }}
                >
                  <Typography
                    variant="body2"
                    sx={{ color: 'text.headline', whiteSpace: 'pre-wrap', lineHeight: 1.5 }}
                  >
                    {data.notes}
                  </Typography>
                </Box>
              </Box>
            )}

            {/* Patient-uploaded documents (from the public tracking link) */}
            <Box sx={{ mb: 3.5 }}>
              <SectionHeader label={t('kanban.documents')} />
              {data.nationalIdImageUrl || data.insuranceIdImageUrl ? (
                <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                  {data.nationalIdImageUrl && (
                    <DocumentRow label={t('kanban.nationalIdDoc')} url={data.nationalIdImageUrl} />
                  )}
                  {data.insuranceIdImageUrl && (
                    <DocumentRow label={t('kanban.insuranceDoc')} url={data.insuranceIdImageUrl} />
                  )}
                </Box>
              ) : (
                <Typography variant="body2" sx={{ color: 'text.secondary' }}>
                  {t('kanban.noDocuments')}
                </Typography>
              )}
            </Box>

            {/* Items */}
            <Box sx={{ mb: 3.5 }}>
              <Box sx={{ mb: 1.5 }}>
                <Eyebrow>{t('kanban.items')}</Eyebrow>
              </Box>
              <Box sx={{ height: '1px', bgcolor: 'divider', mb: 1.5 }} />
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.25 }}>
                {data.items.map((item) => (
                  <ItemRow key={item.id} item={item} />
                ))}
              </Box>
            </Box>

            {/* Timeline */}
            <Box sx={{ mb: 2 }}>
              <SectionHeader label={t('kanban.statusTimeline')} />
              <Box sx={{ position: 'relative', pl: 2.5 }}>
                <Box
                  sx={{
                    position: 'absolute',
                    left: 6,
                    top: 4,
                    bottom: 4,
                    width: '2px',
                    bgcolor: 'divider',
                  }}
                />
                {data.statusLogs.map((log, idx) => {
                  const s = statusStyle(log.newStatus);
                  const isLatest = idx === 0;
                  return (
                    <Box
                      key={idx}
                      sx={{ position: 'relative', pb: idx === data.statusLogs.length - 1 ? 0 : 1.75 }}
                    >
                      <Box
                        sx={{
                          position: 'absolute',
                          left: -18,
                          top: 2,
                          width: 14,
                          height: 14,
                          borderRadius: '50%',
                          bgcolor: isLatest ? s.fg : 'background.paper',
                          border: '2px solid',
                          borderColor: s.fg,
                          boxShadow: isLatest ? `0 0 0 4px ${s.bg}` : 'none',
                        }}
                      />
                      <Typography
                        sx={{
                          fontFamily: '"Plus Jakarta Sans", sans-serif',
                          fontWeight: 600,
                          fontSize: '0.85rem',
                          color: isLatest ? 'text.headline' : 'text.secondary',
                          lineHeight: 1.3,
                        }}
                      >
                        {s.label}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{
                          color: 'text.disabled',
                          fontVariant: 'tabular-nums',
                        }}
                      >
                        {new Date(log.createdAt).toLocaleString('es-DO', {
                          day: 'numeric',
                          month: 'short',
                          hour: '2-digit',
                          minute: '2-digit',
                        })}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </Box>

            {/* Footer meta */}
            <Box sx={{ pt: 1, borderTop: '1px solid', borderColor: 'divider' }}>
              <Typography
                variant="caption"
                sx={{ color: 'text.disabled', display: 'block', fontVariant: 'tabular-nums' }}
              >
                {t('kanban.createdAt')}: {new Date(data.createdAt).toLocaleString()}
              </Typography>
              {data.completedAt && (
                <Typography
                  variant="caption"
                  sx={{ color: 'text.disabled', display: 'block', fontVariant: 'tabular-nums' }}
                >
                  {t('kanban.completedAt')}: {new Date(data.completedAt).toLocaleString()}
                </Typography>
              )}
            </Box>
          </>
        )}
      </Box>
    </Drawer>
  );
};

const isPdf = (url: string) => /\.pdf(\?|#|$)/i.test(url);

const DocumentRow: React.FC<{ label: string; url: string }> = ({ label, url }) => {
  const { t } = useTranslation();
  const pdf = isPdf(url);
  return (
    <Box
      component="a"
      href={url}
      target="_blank"
      rel="noopener noreferrer"
      aria-label={label}
      sx={{
        display: 'flex',
        alignItems: 'center',
        gap: 1.5,
        p: 1.5,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        textDecoration: 'none',
        color: 'inherit',
        transition: 'border-color 160ms ease-out, box-shadow 160ms ease-out',
        '&:hover': {
          borderColor: 'primary.main',
          boxShadow: '0 2px 10px rgba(13,27,53,0.06)',
        },
      }}
    >
      {pdf ? (
        <Box
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            flexShrink: 0,
            bgcolor: 'primary.softBg',
            color: 'primary.dark',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <DescriptionRoundedIcon sx={{ fontSize: 22 }} />
        </Box>
      ) : (
        <Box
          component="img"
          src={url}
          alt={label}
          sx={{
            width: 48,
            height: 48,
            borderRadius: 1.5,
            flexShrink: 0,
            objectFit: 'cover',
            border: '1px solid',
            borderColor: 'divider',
          }}
        />
      )}
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          sx={{
            fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
            fontWeight: 500,
            fontSize: '1rem',
            lineHeight: 1.25,
            color: 'text.headline',
          }}
        >
          {label}
        </Typography>
        <Typography variant="caption" sx={{ color: 'primary.dark' }}>
          {t('kanban.viewDocument')}
        </Typography>
      </Box>
      <OpenInNewRoundedIcon sx={{ fontSize: 18, color: 'text.secondary', flexShrink: 0 }} />
    </Box>
  );
};

interface ItemRowProps {
  item: PharmacyChainOrderItem;
}

const ItemRow: React.FC<ItemRowProps> = ({ item }) => {
  const { t } = useTranslation();

  return (
    <Box
      sx={{
        p: 1.75,
        borderRadius: 2,
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'divider',
      }}
    >
      <Typography
        sx={{
          fontFamily: '"Bricolage Grotesque", "Plus Jakarta Sans", sans-serif',
          fontWeight: 500,
          fontSize: '1rem',
          lineHeight: 1.25,
          color: 'text.headline',
        }}
      >
        {item.pharmacyProduct.name}
      </Typography>
      <Box sx={{ display: 'flex', gap: 2, mt: 0.5, color: 'text.secondary' }}>
        <Typography variant="caption">
          {t('kanban.quantity')}: <b>{item.quantity}</b>
        </Typography>
        {item.durationDays != null && (
          <Typography variant="caption">
            {t('kanban.duration')}: <b>{item.durationDays}</b>
          </Typography>
        )}
      </Box>
      {item.instructions && (
        <Typography
          variant="caption"
          sx={{ color: 'text.secondary', display: 'block', mt: 0.5, fontStyle: 'italic' }}
        >
          {item.instructions}
        </Typography>
      )}
    </Box>
  );
};
